/**
 * useTextToSpeech hook
 *
 * Converts text to spoken audio via the OpenAI TTS backend endpoint.
 * Manages an internal HTMLAudioElement for playback, with queue support
 * so callers can enqueue multiple utterances.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { synthesizeSpeech } from '../services/copilotApi';

/* eslint-disable react-hooks/immutability --
   This hook imperatively controls ONE persistent HTMLAudioElement held in a ref:
   it re-points `.src`, sets `.volume`/`.playbackRate`, and attaches on-handlers so
   the element a user gesture unlocked is the SAME one a later (delayed) TTS reply
   auto-plays on — iOS grants autoplay "user activation" PER element. The
   React-Compiler `react-hooks/immutability` rule flags mutating a ref-held value,
   which is a false positive for a media element controlled imperatively. */

export interface UseTextToSpeechReturn {
  /** Whether audio is currently playing OR being synthesized (the whole "busy" span) */
  isSpeaking: boolean;
  /** Whether the TTS backend round-trip is in flight (synthesizing, not yet playing).
   *  Lets the UI show a distinct "preparing voice…" state before "playing". */
  isSynthesizing: boolean;
  /** Whether playback is paused */
  isPaused: boolean;
  /** Error message if TTS failed */
  error: string | null;
  /** Speak text — queues if already speaking */
  speak: (text: string) => void;
  /** Stop playback immediately and clear queue */
  stop: () => void;
  /** Pause current playback */
  pause: () => void;
  /** Resume paused playback */
  resume: () => void;
  /** Unlock audio autoplay — call inside a user gesture (a click/tap) so a later,
   *  delayed TTS reply isn't blocked by the browser autoplay policy. */
  prime: () => void;
}

// A tiny (8-sample, silent) WAV. Playing it inside a user gesture establishes the
// page's audio "user activation" so subsequent (delayed) auto-plays are allowed.
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQgAAACAgICAgICAgA==';

export interface UseTextToSpeechOptions {
  /** TTS voice (default: "nova") */
  voice?: string;
  /** Playback speed multiplier on the audio element (default 1; pitch preserved).
   *  Changing it live re-rates any currently-playing utterance. */
  rate?: number;
  /** TTS model forwarded to the backend (default: the backend default, tts-1). */
  model?: string;
  /** TTS provider forwarded to the backend ('openai' default, or 'google'). */
  provider?: string;
  /** Callback fired when an utterance finishes playing */
  onComplete?: () => void;
  /** Callback fired when TTS errors */
  onError?: (error: string) => void;
}

/** Fallback: speak text using the browser's built-in SpeechSynthesis API */
function speakWithBrowserTTS(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1.0;
    utterance.onend = () => resolve();
    utterance.onerror = () => reject(new Error('Browser TTS failed'));
    speechSynthesis.speak(utterance);
  });
}

export function useTextToSpeech(
  options: UseTextToSpeechOptions = {},
): UseTextToSpeechReturn {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<string[]>([]);
  const activeRef = useRef(false);
  // Cache of synthesized audio object-URLs, keyed by provider|voice|text, so
  // replaying the same utterance (e.g. tapping ▶ on a rundown you just heard, or
  // the auto-play of a reply you then replay) is INSTANT instead of re-hitting
  // the TTS backend. Bounded; URLs are revoked on eviction and on unmount.
  const audioCacheRef = useRef<Map<string, string>>(new Map());

  // Keep options refs fresh without re-creating callbacks
  const onCompleteRef = useRef(options.onComplete);
  onCompleteRef.current = options.onComplete;
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const voiceRef = useRef(options.voice ?? 'nova');
  voiceRef.current = options.voice ?? 'nova';
  const modelRef = useRef(options.model);
  modelRef.current = options.model;
  const providerRef = useRef(options.provider);
  providerRef.current = options.provider;
  // Playback speed (pitch preserved). Synced from options.rate below; new audio
  // elements pick it up and a live change re-rates the currently-playing one.
  const rateRef = useRef(options.rate ?? 1);
  // Monotonic token: stop() / a newer utterance bumps it so an in-flight
  // synthesis that got superseded mid-await drops its audio instead of playing
  // on top of the newer one — this is what kills the "spoken reply repeats over
  // and over" overlap.
  const genRef = useRef(0);

  // Clean up audio element and all cached object URLs on unmount
  useEffect(() => {
    const cache = audioCacheRef.current;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
      queueRef.current = [];
      activeRef.current = false;
    };
  }, []);

  // Keep the playback rate in sync with the option, and apply it to any audio
  // already playing so a speed-slider change is heard immediately.
  useEffect(() => {
    const r = options.rate ?? 1;
    rateRef.current = r;
    if (audioRef.current) audioRef.current.playbackRate = r;
  }, [options.rate]);

  const playNext = useCallback(async () => {
    if (activeRef.current) return;
    const nextText = queueRef.current.shift();
    if (!nextText) {
      setIsSpeaking(false);
      setIsPaused(false);
      return;
    }

    activeRef.current = true;
    const gen = genRef.current;
    setIsSpeaking(true);
    setIsPaused(false);
    setError(null);

    try {
      // Reuse a previously-synthesized clip for the same provider/voice/text so
      // a replay (or re-hearing an auto-played reply) is instant — only hit the
      // TTS backend on a cache miss.
      const cacheKey = `${providerRef.current ?? 'openai'}|${voiceRef.current}|${nextText}`;
      let url = audioCacheRef.current.get(cacheKey);
      if (url === undefined) {
        setIsSynthesizing(true); // backend round-trip in flight (not a cache hit)
        const blob = await synthesizeSpeech(
          nextText,
          voiceRef.current,
          modelRef.current,
          providerRef.current,
        );
        setIsSynthesizing(false); // audio is ready; about to play
        // A stop() or newer utterance landed while we were synthesizing — drop
        // this audio rather than play it over the newer one.
        if (gen !== genRef.current) return;
        url = URL.createObjectURL(blob);
        audioCacheRef.current.set(cacheKey, url);
        // Bound the cache; revoke the oldest evicted clip's URL.
        if (audioCacheRef.current.size > 16) {
          const oldestKey = audioCacheRef.current.keys().next().value as string;
          const oldUrl = audioCacheRef.current.get(oldestKey);
          audioCacheRef.current.delete(oldestKey);
          if (oldUrl) URL.revokeObjectURL(oldUrl);
        }
      } else if (gen !== genRef.current) {
        // Superseded before this cached playback even started.
        return;
      }

      // Reuse ONE persistent <audio> element across utterances. iOS grants
      // autoplay "user activation" PER-ELEMENT, so the element that prime()
      // unlocked during the first tap must be the SAME one we re-point here —
      // otherwise the first auto-played rundown is silently blocked on phones
      // (the old code built a fresh `new Audio(url)` each time, which the prime
      // unlock never carried to). Re-pointing `.src` keeps the activation.
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.src = url;
      audio.volume = 1; // prime() may have left it at 0
      // Speed (pitch preserved). preservesPitch is standard now; the vendor
      // fallbacks cover older engines.
      const rated = audio as HTMLAudioElement & {
        preservesPitch?: boolean;
        mozPreservesPitch?: boolean;
        webkitPreservesPitch?: boolean;
      };
      rated.preservesPitch = true;
      rated.mozPreservesPitch = true;
      rated.webkitPreservesPitch = true;
      audio.playbackRate = rateRef.current;

      audio.onended = () => {
        activeRef.current = false;
        // Keep the persistent element (its iOS audio activation is reused);
        // just advance the queue or signal completion.
        if (queueRef.current.length > 0) {
          playNext();
        } else {
          setIsSpeaking(false);
          setIsPaused(false);
          onCompleteRef.current?.();
        }
      };

      audio.onerror = () => {
        const msg = 'Audio playback failed.';
        setError(msg);
        activeRef.current = false;
        setIsSpeaking(false);
        setIsPaused(false);
        onErrorRef.current?.(msg);
      };

      await audio.play();
    } catch {
      setIsSynthesizing(false);
      // OpenAI TTS failed (network error, rate limit, etc.)
      // If a stop()/newer utterance superseded us mid-flight, don't fall back.
      if (gen !== genRef.current) return;
      // Fall back to browser SpeechSynthesis if available
      if (typeof speechSynthesis !== 'undefined') {
        try {
          await speakWithBrowserTTS(nextText);
          activeRef.current = false;
          if (queueRef.current.length > 0) {
            playNext();
          } else {
            setIsSpeaking(false);
            setIsPaused(false);
            onCompleteRef.current?.();
          }
          return;
        } catch {
          // Browser TTS also failed — fall through to error
        }
      }

      const msg = 'Text-to-speech unavailable. Check your connection.';
      setError(msg);
      activeRef.current = false;
      setIsSpeaking(false);
      setIsPaused(false);
      onErrorRef.current?.(msg);
    }
  }, []);

  const speak = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      queueRef.current.push(trimmed);
      if (!activeRef.current) {
        playNext();
      }
    },
    [playNext],
  );

  const stop = useCallback(() => {
    // Supersede any in-flight synthesis so it drops its audio when it resolves.
    genRef.current += 1;
    queueRef.current = [];
    activeRef.current = false;
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      // Keep the element instance — nulling it would discard the iOS audio
      // activation that lets the NEXT reply auto-play.
    }
    // NB: do NOT revoke cached object URLs here — they are reused for instant
    // replay and are freed on eviction / unmount.
    setIsSpeaking(false);
    setIsSynthesizing(false);
    setIsPaused(false);
    setError(null);
  }, []);

  const prime = useCallback(() => {
    // Unlock autoplay on the SINGLE persistent <audio> element that playback
    // reuses: play a tiny silent clip on it INSIDE the user gesture so iOS grants
    // THAT element "user activation" and a later (delayed) TTS reply auto-plays on
    // the same element. (The old code primed a throwaway element, so the unlock
    // never reached the element that actually spoke — the cause of "the first one
    // doesn't play; I tap play and then it works.") Best-effort and silent.
    if (activeRef.current) return; // never interrupt an utterance in progress
    try {
      const a = audioRef.current ?? new Audio();
      audioRef.current = a;
      a.src = SILENT_AUDIO;
      a.volume = 0;
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.then(() => a.pause()).catch(() => {});
      }
    } catch {
      /* ignore — unlock is best-effort */
    }
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPaused(true);
    }
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play();
      setIsPaused(false);
    }
  }, []);

  return {
    isSpeaking,
    isSynthesizing,
    isPaused,
    error,
    speak,
    stop,
    pause,
    resume,
    prime,
  };
}
